import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import * as Styled from './OCardCarouselCombo.styles';
import { OCardCarouselComboProps } from './OCardCarouselCombo.types';
import { MContentHeader } from '../../molecules/MContentHeader/index';
import { MCarouselControls } from '../../molecules/MCarouselControls/index';
import { MSlot } from '../../molecules/MSlot/index';
import {
  MContentHeaderAlignment,
  MContentHeaderAllowedButtonType,
} from '../../molecules/MContentHeader/MContentHeader.types';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardCarouselCombo';
const TEST_ID = 'mi-web-ui-kit-OCardCarouselCombo';

export const OCardCarouselCombo = (props: OCardCarouselComboProps) => {
  const swiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const [totalSlides, setTotalSlides] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  const {
    peeking = false,
    mContentHeader,
    mCarouselControls,
    pagePath = '',
    itemPath = '',
    cqPath,
    isAuthorMode = false,
    ':items': cqItems = {},
    aemReactComponentMapping = {},
    ':itemsOrder': itemsOrder = [],
    className = '',
  } = props;

  const items = cqItems || {};
  const model = { ...props, ':order': cqItems[':itemsOrder'] };
  useEffect(() => {
    if (swiperRef.current?.swiper) {
      setTotalSlides(swiperRef.current.swiper.slides.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cqItems]);

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentSlide(swiper.activeIndex);
  };

  const handlePrev = () => {
    swiperRef.current?.swiper.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current?.swiper.slideNext();
  };

  const getSlidesPerView = () => {
    if (peeking) {
      return 'auto';
    }
    return 1;
  };

  const getSpaceBetween = () => {
    return peeking ? 8 : 16;
  };

  return (
    <Styled.Wrapper className={className} data-component-name={COMPONENT_NAME} data-testid={TEST_ID} $peeking={peeking}>
      {mContentHeader && (
        <Styled.HeaderWrapper>
          <MContentHeader
            alignment={mContentHeader.alignment as MContentHeaderAlignment}
            mTextBlockSimple={mContentHeader.mTextBlockSimple}
            cta={mContentHeader.cta as 'true' | 'false' | undefined}
            mButton={
              mContentHeader.mButton
                ? {
                    ...mContentHeader.mButton,
                    buttonType: mContentHeader.mButton.buttonType as MContentHeaderAllowedButtonType,
                    externalLink: mContentHeader.mButton.externalLink === 'true',
                    openInANewTab: mContentHeader.mButton.openInANewTab === 'true',
                  }
                : undefined
            }
            secondaryCta={mContentHeader.secondaryCta as 'true' | 'false' | undefined}
            mButton2={
              mContentHeader.mButton2
                ? {
                    ...mContentHeader.mButton2,
                    buttonType: mContentHeader.mButton2.buttonType as MContentHeaderAllowedButtonType,
                    externalLink: mContentHeader.mButton2.externalLink === 'true',
                    openInANewTab: mContentHeader.mButton2.openInANewTab === 'true',
                  }
                : undefined
            }
          />
        </Styled.HeaderWrapper>
      )}

      <Styled.CarouselContainer>
        <Styled.SwiperWrapper $peeking={peeking}>
          <Swiper
            ref={swiperRef}
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={getSpaceBetween()}
            slidesPerView={getSlidesPerView()}
            onSwiper={(swiper: SwiperType) => {
              setTotalSlides(swiper.slides.length);
            }}
            onSlideChange={handleSlideChange}
            breakpoints={{
              320: {
                slidesPerView: peeking ? 'auto' : 1,
                spaceBetween: getSpaceBetween(),
              },
              768: {
                slidesPerView: peeking ? 'auto' : 2,
                spaceBetween: 16,
              },
              992: {
                slidesPerView: peeking ? 'auto' : 3,
                spaceBetween: 16,
              },
            }}
          >
            {itemsOrder.length > 0 ? (
              itemsOrder.map((key: string) => {
                const itemData = items[key];

                if (!itemData) return null;

                type AemChild = {
                  ':items'?: Record<string, AemChild>;
                  ':itemsOrder'?: string[];
                };

                type AemModel = {
                  ':items'?: Record<string, AemChild>;
                  ':itemsOrder'?: string[];
                  ':type'?: string;
                  itemPath?: string;
                  pagePath?: string;
                };

                const typedModel = model as AemModel;

                return (
                  <SwiperSlide key={key}>
                    <Styled.SlideContent $peeking={peeking}>
                      <MSlot
                        pagePath={pagePath}
                        itemPath={`${itemPath}/:items/${key}`}
                        isAuthorMode={isAuthorMode}
                        cqItems={typedModel[':items']?.[key]?.[':items'] ?? {}}
                        model={{
                          ...typedModel,
                          ':order': typedModel[':items']?.[key]?.[':itemsOrder'] ?? [],
                          cqItems: typedModel[':items'] ?? {},
                          cqItemsOrder: typedModel[':itemsOrder'] ?? [],
                          cqType: typedModel[':type'],
                          itemPath: typedModel.itemPath,
                          pagePath: typedModel.pagePath,
                        }}
                        aemReactComponentMapping={aemReactComponentMapping}
                      />
                    </Styled.SlideContent>
                  </SwiperSlide>
                );
              })
            ) : (
              <SwiperSlide>
                <Styled.SlideContent $peeking={peeking}>
                  <MSlot
                    pagePath={pagePath}
                    itemPath={`${cqPath || itemPath}/:items`}
                    isAuthorMode={isAuthorMode}
                    cqItems={{}}
                    aemReactComponentMapping={aemReactComponentMapping}
                  />
                </Styled.SlideContent>
              </SwiperSlide>
            )}
          </Swiper>
        </Styled.SwiperWrapper>

        {mCarouselControls && totalSlides > 1 && (
          <Styled.ControlsWrapper $imageOverlay={mCarouselControls.imageOverlay === 'true'}>
            <MCarouselControls
              type={mCarouselControls.type as MCarouselControlsType}
              imageOverlay={mCarouselControls.imageOverlay === 'true'}
              totalSlides={totalSlides}
              currentSlide={currentSlide}
              onPrev={handlePrev}
              onNext={handleNext}
              aIcon={
                mCarouselControls.aIcon?.icon
                  ? {
                      icon: mCarouselControls.aIcon.icon,
                      size: mCarouselControls.aIcon.size as AIconSize | undefined,
                    }
                  : undefined
              }
            />
          </Styled.ControlsWrapper>
        )}
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};

export default OCardCarouselCombo;
